// Conference Rooms — named SIP conference bridges.
// Each room has a dialable extension, optional PIN, and live moderator controls.

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Trash2, X, Edit2, Users, MicOff, Lock, Unlock,
  UserMinus, Phone, RefreshCw, KeyRound,
} from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

async function fetchJson(url: string, opts?: RequestInit) {
  const res = await fetch(url, {
    ...opts,
    headers: { ...auth().headers, ...(opts?.headers || {}), ...(opts?.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || res.statusText);
  }
  return res.json();
}

interface Room {
  room_uuid: string;
  domain_uuid: string;
  room_name: string;
  extension: string;
  has_member_pin: boolean;
  has_moderator_pin: boolean;
  max_members: number;
  record_calls: boolean;
  created_at: string;
}

interface Participant {
  member_id: string;
  caller_id_name: string;
  caller_id_number: string;
  muted: boolean;
  deaf: boolean;
  talking: boolean;
  hold: boolean;
  talk_time: number;
  flags: string[];
}

function formatTalkTime(seconds: number) {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── Room edit modal ───────────────────────────────────────────────────────────
function RoomModal({
  room, domainUuid, onClose, onSave,
}: {
  room: Partial<Room> | null;
  domainUuid: string;
  onClose: () => void;
  onSave: () => void;
}) {
  const toast = useToast();
  const isNew = !room?.room_uuid;

  const [form, setForm] = useState({
    room_name: room?.room_name || '',
    extension: room?.extension || '',
    member_pin: '',
    moderator_pin: '',
    max_members: room?.max_members ?? 10,
    record_calls: room?.record_calls ?? false,
  });
  const [saving, setSaving] = useState(false);

  function set(k: string, v: any) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.room_name.trim()) return toast('error', 'Room name required');
    if (!form.extension.trim()) return toast('error', 'Extension required');
    setSaving(true);
    try {
      const body: any = {
        domain_uuid: domainUuid,
        room_name: form.room_name.trim(),
        extension: form.extension.trim(),
        max_members: Number(form.max_members) || 10,
        record_calls: form.record_calls,
      };
      // Only send PIN fields if they have a value (empty string = clear)
      if (form.member_pin !== '' || isNew) body.member_pin = form.member_pin;
      if (form.moderator_pin !== '' || isNew) body.moderator_pin = form.moderator_pin;

      if (isNew) {
        await fetchJson('/api/conference-rooms', { method: 'POST', body: JSON.stringify(body) });
        toast('success', 'Room created');
      } else {
        await fetchJson(`/api/conference-rooms/${room!.room_uuid}`, { method: 'PUT', body: JSON.stringify(body) });
        toast('success', 'Room updated');
      }
      onSave();
      onClose();
    } catch (e: any) {
      toast('error', e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-bgCard border border-border rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-lg font-semibold">{isNew ? 'New Conference Room' : 'Edit Room'}</h2>
          <button onClick={onClose} className="text-textSecondary hover:text-text p-1 rounded">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Room Name</label>
            <input
              className="input w-full"
              placeholder="e.g. Main Conference"
              value={form.room_name}
              onChange={e => set('room_name', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Extension (dial to join)</label>
            <input
              className="input w-full font-mono"
              placeholder="e.g. 7100"
              value={form.extension}
              onChange={e => set('extension', e.target.value.replace(/[^0-9*#]/g, ''))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Member PIN
                {!isNew && <span className="text-textSecondary ml-1 font-normal">(leave blank to keep)</span>}
              </label>
              <input
                className="input w-full font-mono"
                type="password"
                placeholder={!isNew ? '••••' : 'optional'}
                value={form.member_pin}
                onChange={e => set('member_pin', e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Moderator PIN
                {!isNew && <span className="text-textSecondary ml-1 font-normal">(leave blank to keep)</span>}
              </label>
              <input
                className="input w-full font-mono"
                type="password"
                placeholder={!isNew ? '••••' : 'optional'}
                value={form.moderator_pin}
                onChange={e => set('moderator_pin', e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Max Members</label>
              <input
                className="input w-full"
                type="number"
                min="1"
                max="999"
                value={form.max_members}
                onChange={e => set('max_members', e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                id="record_calls"
                type="checkbox"
                checked={form.record_calls}
                onChange={e => set('record_calls', e.target.checked)}
                className="rounded"
              />
              <label htmlFor="record_calls" className="text-sm cursor-pointer select-none">
                Record calls
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5 pt-1">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : (isNew ? 'Create Room' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Live participants panel ────────────────────────────────────────────────────
function ParticipantsPanel({
  room, domainUuid, onClose,
}: {
  room: Room;
  domainUuid: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [locked, setLocked] = useState(false);
  const [actioning, setActioning] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['conf-participants', room.room_uuid],
    queryFn: () => fetchJson(`/api/conference-rooms/${room.room_uuid}/participants?domain_uuid=${domainUuid}`),
    refetchInterval: 5000,
  });

  const participants: Participant[] = data?.participants || [];
  const active: boolean = data?.active || false;

  async function action(path: string, label: string, extraBody?: any) {
    setActioning(label);
    try {
      await fetchJson(`/api/conference-rooms/${room.room_uuid}/${path}`, {
        method: 'POST',
        body: JSON.stringify({ domain_uuid: domainUuid, ...extraBody }),
      });
      refetch();
    } catch (e: any) {
      toast('error', e.message || `${label} failed`);
    } finally {
      setActioning(null);
    }
  }

  async function kick(memberId: string) {
    await action(`kick/${memberId}`, `kick-${memberId}`);
  }

  async function toggleLock() {
    const next = !locked;
    await action(next ? 'lock' : 'unlock', next ? 'lock' : 'unlock');
    setLocked(next);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-bgCard border border-border rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users size={18} />
              {room.room_name}
            </h2>
            <p className="text-sm text-textSecondary mt-0.5">
              Extension <span className="font-mono">{room.extension}</span>
              {active ? (
                <span className="ml-2 text-green-400 font-medium">Active</span>
              ) : (
                <span className="ml-2 text-textSecondary">No active conference</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-secondary text-xs flex items-center gap-1"
              onClick={() => refetch()}
              title="Refresh"
            >
              <RefreshCw size={13} />
            </button>
            <button onClick={onClose} className="text-textSecondary hover:text-text p-1 rounded">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Moderator action bar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-bgHover flex-shrink-0">
          <button
            className="btn-secondary text-xs flex items-center gap-1"
            onClick={() => action('mute-all', 'mute-all')}
            disabled={!active || !!actioning}
            title="Mute all participants"
          >
            <MicOff size={13} />
            Mute All
          </button>
          <button
            className={`btn-secondary text-xs flex items-center gap-1 ${locked ? 'border-amber-500 text-amber-400' : ''}`}
            onClick={toggleLock}
            disabled={!active || !!actioning}
            title={locked ? 'Unlock room' : 'Lock room (no new joins)'}
          >
            {locked ? <Unlock size={13} /> : <Lock size={13} />}
            {locked ? 'Unlock Room' : 'Lock Room'}
          </button>
          <span className="ml-auto text-sm text-textSecondary">
            {isLoading ? 'Loading...' : `${participants.length} participant${participants.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* Participants list */}
        <div className="overflow-y-auto flex-1">
          {isLoading ? (
            <div className="p-8 text-center text-textSecondary">Loading...</div>
          ) : participants.length === 0 ? (
            <div className="p-8 text-center text-textSecondary">
              {active ? 'No participants' : 'Conference is not active. Dial ' + room.extension + ' to start.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-textSecondary text-xs">
                  <th className="text-left px-5 py-2">Caller</th>
                  <th className="text-left px-3 py-2">Number</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Talk Time</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {participants.map(p => (
                  <tr key={p.member_id} className="border-b border-border/50 hover:bg-bgHover">
                    <td className="px-5 py-2.5 font-medium">
                      {p.caller_id_name || <span className="text-textSecondary italic">Unknown</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-textSecondary">
                      {p.caller_id_number}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                        p.muted
                          ? 'bg-red-500/15 text-red-400'
                          : p.talking
                          ? 'bg-green-500/15 text-green-400'
                          : 'bg-bgHover text-textSecondary'
                      }`}>
                        {p.muted ? 'Muted' : p.talking ? 'Speaking' : 'Listening'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-textSecondary">
                      {formatTalkTime(p.talk_time)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500/10 transition-colors"
                        title="Kick participant"
                        onClick={() => kick(p.member_id)}
                        disabled={!!actioning}
                      >
                        <UserMinus size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ConferenceRooms() {
  const me = api.me();
  const qc = useQueryClient();
  const toast = useToast();
  const domainUuid = me?.domain_uuid || '';

  const [editing, setEditing] = useState<Partial<Room> | null>(null);
  const [viewRoom, setViewRoom] = useState<Room | null>(null);

  const { data: rooms = [], isLoading } = useQuery<Room[]>({
    queryKey: ['conference-rooms', domainUuid],
    queryFn: () => domainUuid
      ? fetchJson(`/api/conference-rooms?domain_uuid=${domainUuid}`)
      : Promise.resolve([]),
    enabled: !!domainUuid,
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/conference-rooms/${id}?domain_uuid=${domainUuid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conference-rooms', domainUuid] }),
    onError: (e: any) => toast('error', e?.message || 'Could not delete room'),
  });

  function onSaved() {
    qc.invalidateQueries({ queryKey: ['conference-rooms', domainUuid] });
  }

  function confirmDelete(room: Room) {
    if (!window.confirm(`Delete "${room.room_name}" (ext ${room.extension})? This cannot be undone.`)) return;
    remove.mutate(room.room_uuid);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Users size={22} />
          Conference Rooms
        </h1>
        <button className="btn-primary flex items-center gap-1" onClick={() => setEditing({})}>
          <Plus size={16} />
          New Room
        </button>
      </div>

      <div className="card p-3 text-sm text-textSecondary">
        Named SIP conference bridges. Dial the extension to join a room. Rooms with a member PIN
        challenge callers before entry. Use the participants view for live moderator controls.
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-textSecondary">Loading...</div>
      ) : rooms.length === 0 ? (
        <div className="card p-8 text-center text-textSecondary">
          No conference rooms yet.{' '}
          <button className="text-primary hover:underline" onClick={() => setEditing({})}>
            Create one
          </button>
          .
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-textSecondary text-xs">
                <th className="text-left px-5 py-3">Room Name</th>
                <th className="text-left px-4 py-3">Extension</th>
                <th className="text-center px-4 py-3">Member PIN</th>
                <th className="text-center px-4 py-3">Mod PIN</th>
                <th className="text-center px-4 py-3">Max</th>
                <th className="text-center px-4 py-3">Record</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rooms.map(room => (
                <tr
                  key={room.room_uuid}
                  className="border-b border-border/50 hover:bg-bgHover transition-colors"
                >
                  <td className="px-5 py-3 font-medium">{room.room_name}</td>
                  <td className="px-4 py-3 font-mono">{room.extension}</td>
                  <td className="px-4 py-3 text-center">
                    {room.has_member_pin ? (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                        <KeyRound size={11} /> Set
                      </span>
                    ) : (
                      <span className="text-textSecondary text-xs">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {room.has_moderator_pin ? (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                        <KeyRound size={11} /> Set
                      </span>
                    ) : (
                      <span className="text-textSecondary text-xs">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-textSecondary">{room.max_members}</td>
                  <td className="px-4 py-3 text-center">
                    {room.record_calls ? (
                      <span className="text-green-400 text-xs font-medium">Yes</span>
                    ) : (
                      <span className="text-textSecondary text-xs">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        className="text-textSecondary hover:text-text p-1.5 rounded hover:bg-bgHover transition-colors"
                        title="View participants / moderator controls"
                        onClick={() => setViewRoom(room)}
                      >
                        <Phone size={14} />
                      </button>
                      <button
                        className="text-textSecondary hover:text-text p-1.5 rounded hover:bg-bgHover transition-colors"
                        title="Edit room"
                        onClick={() => setEditing(room)}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-red-500/10 transition-colors"
                        title="Delete room"
                        onClick={() => confirmDelete(room)}
                        disabled={remove.isPending}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <RoomModal
          room={editing}
          domainUuid={domainUuid}
          onClose={() => setEditing(null)}
          onSave={onSaved}
        />
      )}

      {viewRoom && (
        <ParticipantsPanel
          room={viewRoom}
          domainUuid={domainUuid}
          onClose={() => setViewRoom(null)}
        />
      )}
    </div>
  );
}
